import React, { useEffect, useMemo, useState } from 'react';
import roleApi from '../../../../services/roleApi';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import { toast } from 'react-hot-toast';
import {
  Users,
  Calendar,
  Shield,
  Clock,
  CheckCircle,
  XCircle,
  Check,
  X,
  Filter
} from 'lucide-react';
import './RoleRequestsPanel.css';

const RoleRequestsPanel = () => {
  const [roleType, setRoleType] = useState('');
  const [status, setStatus] = useState('PENDING');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [workingId, setWorkingId] = useState(null);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const params = {};
      if (roleType) params.roleType = roleType;
      if (status) params.status = status;
      const res = await roleApi.getAllRoles(params);
      const list = res?.data ?? res ?? [];
      setRows(list);
    } catch (err) {
      console.error('Failed to load role requests', err);
      toast.error(err?.message || 'Failed to load role requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleType, status]);

  const statusLabel = useMemo(
    () => ({
      PENDING: 'Pending',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
    }),
    []
  );

  const decide = async (id, decision) => {
    setWorkingId(id);
    try {
      await roleApi.decide(id, decision, null);
      toast.success(decision === 'APPROVE' ? 'Request approved' : 'Request rejected');
      fetchRoles();
    } catch (err) {
      console.error('Failed to decide request', err);
      toast.error(err?.message || 'Unable to update request');
    } finally {
      setWorkingId(null);
    }
  };

  const getStatusIcon = (statusStr) => {
    if (statusStr === 'APPROVED') return <CheckCircle size={14} />;
    if (statusStr === 'REJECTED') return <XCircle size={14} />;
    return <Clock size={14} />;
  };

  return (
    <div className="role-requests-shell">
      <div className="role-requests-header">
        <div className="role-requests-title">
          <h3><Users size={24} className="text-blue-500" /> Role Requests</h3>
        </div>
        <div className="role-requests-filter-row">
          <div style={{ position: 'relative' }}>
            <Filter size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', opacity: 0.5, pointerEvents: 'none' }} />
            <select
              className="role-filter-select"
              value={roleType}
              onChange={(e) => setRoleType(e.target.value)}
              style={{ paddingLeft: '2.2rem' }}
            >
              <option value="">All Roles</option>
              <option value="PROMOTER">Promoter</option>
              <option value="STAFF">Staff</option>
              <option value="SALE_AGENT">Sale Agent</option>
            </select>
          </div>

          <select className="role-filter-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '3rem', display: 'flex', justifyContent: 'center' }}>
          <LoadingSpinner text="Loading requests..." />
        </div>
      ) : rows.length === 0 ? (
        <div className="role-empty-state">
          <Shield size={48} style={{ opacity: 0.3 }} />
          <span>No requests found matching your filters.</span>
        </div>
      ) : (
        <div className="role-requests-table">
          <div className="role-requests-row header">
            <div>Requester</div>
            <div>Event Context</div>
            <div>Role Type</div>
            <div>Status</div>
            <div>Date Requested</div>
            <div>Actions</div>
          </div>
          {rows.map((r) => (
            <div className="role-requests-row" key={r.id}>
              <div className="role-cell primary">
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 8, fontSize: '0.8rem', fontWeight: 700 }}>
                  {(r.userName || r.requestedByName || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <div style={{ fontWeight: 500 }}>{r.userName || r.requestedByName || 'Unknown User'}</div>
                  {(r.userEmail) && (
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{r.userEmail}</div>
                  )}
                </div>
              </div>
              <div className="role-cell">
                <Calendar size={14} style={{ opacity: 0.5 }} />
                {r.eventTitle || <span style={{ opacity: 0.5 }}>System-wide</span>}
              </div>
              <div>
                <span className="role-chip role-type">
                  <Shield size={12} />
                  {r.roleType}
                </span>
              </div>
              <div>
                <span className={`role-chip status-${r.status.toLowerCase()}`}>
                  {getStatusIcon(r.status)}
                  {statusLabel[r.status] || r.status}
                </span>
              </div>
              <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
                {r.requestedAt ? new Date(r.requestedAt).toLocaleDateString() : '—'}
              </div>
              <div>
                {r.status === 'PENDING' ? (
                  <div className="role-action-bar">
                    <button
                      className="role-action-btn approve"
                      disabled={workingId === r.id}
                      onClick={() => decide(r.id, 'APPROVE')}
                    >
                      {workingId === r.id ? '...' : <><Check size={14} /> Approve</>}
                    </button>
                    <button
                      className="role-action-btn reject"
                      disabled={workingId === r.id}
                      onClick={() => decide(r.id, 'REJECT')}
                    >
                      {workingId === r.id ? '...' : <><X size={14} /> Reject</>}
                    </button>
                  </div>
                ) : (
                  <span style={{ fontSize: '0.8rem', opacity: 0.5 }}>-</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RoleRequestsPanel;
